import { InfoOutlined } from "@mui/icons-material";
import { Button, Typography } from "@orijinworks/frontend-commons";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { deleteAnnouncementFeed, deleteMessage, getAnnouncementAudit, getConversationAudit, updateConversationSettings } from "../../../../../util/APIUtils";
import { getAnnouncementAuditColumns, getConversationAuditColumns, getFilterValues, getIsDeletedFilter, getLocationTransformedFilter, hasAuditDependenciesChanged, mapResponseToAuditAnnouncementData, mapResponseToAuditConversationData, normalizeAuditDependencies, parseAuditLogResponse, parseConversationAuditLogResponse, updateConversationAuditLogResponse } from "../../services/Audit.service";
import { eventEmitter } from "../../../../services/event-emitter-service";
import { EVENTS } from "../../../../services/constants";
import { LOCATIONS } from "../../../../../constants";
import { handleToggleModal } from "../../services/Feed.service";
import { useNavigate } from "react-router-dom";

/**
 * Custom hook for managing audit log functionality
 * @returns {Object} An object containing audit state and handler functions
 * @returns {Object} return.modalRef - Reference to the delete modal component
 * @returns {Array} return.columns - Array of column definitions for the data grid
 * @returns {Array} return.data - Array of audit log data items
 * @returns {number} return.pageSize - Current page size for pagination
 * @returns {boolean} return.loading - Loading state indicator
 * @returns {string} return.selectedType - Currently selected audit type ('Announcements' or 'Conversations')
 * @returns {number} return.totalRecordsCount - Total number of records available
 * @returns {Function} return.onTogglaModalHandlerHandler - Handler to toggle the delete modal
 * @returns {Function} return.onContinueDeleteModalHandlerHandler - Handler to confirm and execute delete action
 * @returns {Function} return.onFilterChangeHandler - Handler for filter changes
 * @returns {Function} return.onTypeChangeHandler - Handler for audit type changes
 * @returns {Function} return.handleSelectedChangeHandler - Handler for selection changes in data grid
 * @returns {Function} return.refreshContactsHandler - Handler to refresh audit data
 * @returns {Function} return.onPageChangeHandler - Handler for page changes
 * @returns {Function} return.onPageSizeChangeHandler - Handler for page size changes
 * @returns {Function} return.onSortChangeHandler - Handler for sort changes
 */
export const useAuditHook = () => {
    const modalRef = useRef(null);
    const hasLoadedOnce = useRef(false);
    const locationsInitialized = useRef(false);
    const prevLocationsRef = useRef(null);
    const prevSelectedTypeRef = useRef(null);
    const onDeleteHandlerRef = useRef(null);
    const onConversationSettingsUpdateHandlerRef = useRef(null);
    const onViewConversationHandlerRef = useRef(null);
    const prevDepsRef = useRef({});
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedAnnouncementData, setSelectedAnnouncementData] = useState(null);
    const [selectedConversationData, setSelectedConversationData] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [selectedType, setSelectedType] = useState('Conversations');
    const [sortColumn, setSortColumn] = useState('createdAt');
    const [sortDirection, setSortDirection] = useState('desc');
    const [columns, setColumns] = useState([]);
    const [data, setData] = useState([]);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState([]);
    const [totalRecordsCount, setTotalRecordsCount] = useState(0);
    const navigate = useNavigate();
    
    /**
     * Toggles the delete modal visibility
     */
    const onTogglaModalHandlerHandler = useCallback(() => {
        handleToggleModal(modalRef);
    }, []); // modalRef is stable, no dependencies needed
    
    /**
     * Handles the delete announcement action
     * Opens the delete modal and sets the selected announcement ID
     * @param {Object} data - The announcement data object
     * @param {number|string} data.id - The ID of the announcement to delete
     */
    const onDeleteHandler = useCallback((data) => {
        onTogglaModalHandlerHandler();
        setSelectedId(data.id);
    }, [onTogglaModalHandlerHandler]);
    
    // Keep ref in sync with the latest handler
    useEffect(() => {
        onDeleteHandlerRef.current = onDeleteHandler;
    }, [onDeleteHandler]);
  
    /**
     * Handles opening the drawer with announcement data
     * @param {Object} announcementData - The full announcement data object to display
     */
    const handleOpenDrawer = useCallback((announcementData) => {
        setSelectedAnnouncementData(announcementData);
        setDrawerOpen(true);
    }, []);

    /**
     * Handles closing the drawer
     */
    const handleCloseDrawer = useCallback(() => {
        setDrawerOpen(false);
        setSelectedAnnouncementData(null);
        setSelectedConversationData(null);
    }, []);

    /**
     * Handles opening the drawer with conversation data
     * Accepts either a full conversationData object or just an id (messageId)
     * @param {Object|string|number} conversationDataOrId - The full conversation data object or just the messageId
     */
    const handleOpenConversationDrawer = useCallback((conversationDataOrId) => {
        // If it's just an id (string or number), construct minimal object
        if (typeof conversationDataOrId === 'string' || typeof conversationDataOrId === 'number') {
            setSelectedConversationData({ messageId: conversationDataOrId });
        } else {
            // If it's an object, normalize it to ensure it has messageId
            // The audit response uses 'id' for messageId, so map it if needed
            const normalizedData = {
                ...conversationDataOrId,
                messageId: conversationDataOrId.messageId || conversationDataOrId.id,
            };
            setSelectedConversationData(normalizedData);
        }
        setDrawerOpen(true);
    }, []);

    /**
     * Loads announcement audit data from the API
     * Fetches paginated, sorted, and filtered audit log data
     * @async
     * @returns {Promise<void>}
     */
    const loadAnnouncementAudit = useCallback(async () => {
        setLoading(true);
        const response = await getAnnouncementAudit({
            page: currentPage,
            size: pageSize,
            sort: sortColumn + ',' + sortDirection,
            ...getFilterValues(filter),
            ...getLocationTransformedFilter(filter),
            isDeleted: getIsDeletedFilter(filter),
        });
        parseAuditLogResponse(response, filter);
        // Use ref to get the latest handler without including it in dependencies
        setData(response.data.content.map((data) => mapResponseToAuditAnnouncementData(data, onDeleteHandlerRef.current, handleOpenDrawer)));
        setLoading(false);
        setTotalRecordsCount(response.data.totalElements);
    }, [currentPage, pageSize, sortColumn, sortDirection, filter, onDeleteHandler, handleOpenDrawer]);
    
    /**
     * Loads conversations audit data from the API
     * Fetches paginated, sorted, and filtered audit log data
     * @async
     * @returns {Promise<void>}
     */
    const loadConversationsAudit = useCallback(async () => {
        setLoading(true);
        const response = await getConversationAudit({
            page: currentPage,
            size: pageSize,
            sort: sortColumn + ',' + sortDirection,
            ...getFilterValues(filter),
            locationIds: getLocationTransformedFilter(filter),
        });
        if (response) {
            updateConversationAuditLogResponse(response);
            parseConversationAuditLogResponse(response, filter);
            // Use refs to get the latest handlers without including them in dependencies
            setData(response.data.content.map((data) => mapResponseToAuditConversationData(data, onDeleteHandlerRef.current, onConversationSettingsUpdateHandlerRef.current, onViewConversationHandlerRef.current)));
            setLoading(false);
            setTotalRecordsCount(response.data.totalElements);
        }
    }, [currentPage, pageSize, sortColumn, sortDirection, filter]);
    
    /**
     * Handles the conversation settings update action
     * @param {Object} data - The conversation data object
     * @returns {void}
     */
    const onConversationSettingsUpdateHandler = useCallback(async (data) => {
        setLoading(true);
        const response = await updateConversationSettings(data.feedId, !data.isShutdown);
        if (response) {
            await loadConversationsAudit();
        }
        setLoading(false);
    }, [loadConversationsAudit]);
    
    // Keep ref in sync with the latest handler
    useEffect(() => {
        onConversationSettingsUpdateHandlerRef.current = onConversationSettingsUpdateHandler;
    }, [onConversationSettingsUpdateHandler]);

    // Keep ref in sync with the latest handler
    useEffect(() => {
        onViewConversationHandlerRef.current = handleOpenConversationDrawer;
    }, [handleOpenConversationDrawer]);

    /**
     * Loads audit data based on the selected type
     * @async
     * @returns {Promise<void>}
     */
    const loadAudit = useCallback(async () => {
       if (selectedType === 'Announcements') {
        loadAnnouncementAudit();
       } else if (selectedType === 'Conversations') {
        loadConversationsAudit();
       }
    }, [selectedType, loadAnnouncementAudit, loadConversationsAudit]);
    /**
     * Refreshes the audit data by reloading it
     */
    const refreshContactsHandler = useCallback(() => {
        loadAudit();
    }, [loadAudit]);

    /**
     * Handles announcement deletion from the drawer
     * Called after the announcement is deleted by useAnnouncementMenuHook
     * Closes the drawer and refreshes the audit data
     * @param {string|number} announcementId - The ID of the deleted announcement
     */
    const handleDrawerAnnouncementDelete = useCallback((announcementId) => {
        // Close the drawer
        handleCloseDrawer();
        // Refresh the audit data (preserves selectedType)
        loadAudit();
    }, [handleCloseDrawer, loadAudit]);
    
    /**
     * Handles the continue action in the delete modal
     * Deletes the selected announcement and reloads the audit data
     * @async
     * @returns {Promise<void>}
     */
    const onContinueDeleteModalHandlerHandler = useCallback(async () => {
        handleToggleModal(modalRef);
        setLoading(true);
        let response = null;
        if (selectedType === 'Announcements') {
            response = await deleteAnnouncementFeed(selectedId);
        } else if (selectedType === 'Conversations') {
            response = await deleteMessage(selectedId);
        }

        if (response) {
            loadAudit();
        }
    }, [selectedId, loadAudit]);
    
    /**
     * Handles selection changes in the data grid
     * Currently logs the change (placeholder for future implementation)
     */
    const handleSelectedChangeHandler = useCallback(() => {
        console.log('handleSelectedChange');
    }, []);
    
    /**
     * Handles page change in pagination
     * @param {number} page - The new page number (1-indexed)
     */
    const onPageChangeHandler = useCallback((page) => {
        setCurrentPage(page);
    }, []);
    
    /**
     * Handles page size change in pagination
     * Resets to page 1 when page size changes
     * @param {number} pageSize - The new page size
     */
    const onPageSizeChangeHandler = useCallback((pageSize) => {
        setCurrentPage(1);
        setPageSize(pageSize);
    }, []);
    
    /**
     * Handles sort column and direction changes
     * Resets to page 1 when sort changes
     * @param {string} column - The column name to sort by
     * @param {string} direction - The sort direction ('asc' or 'desc')
     */
    const onSortChangeHandler = useCallback((column, direction) => {
        setCurrentPage(1);
        setSortColumn(column);
        setSortDirection(direction);
    }, []);
    
    /**
     * Handles audit type change (e.g., Announcements vs Conversations)
     * @param {Object|null} event - The event object (may be null)
     * @param {string} selectedValue - The newly selected type value
     */
    const onTypeChangeHandler = useCallback((event, selectedValue) => {
        document.querySelector('button[aria-label="Go to page 1"]')?.click(); //ITS AN INTERIM FIX SINCE DT DO NOT SUPPORT DATGRID
        setCurrentPage(1);
        setPageSize(10);
        setSortColumn('createdAt');
        setSortDirection('desc');
        setFilter([]);
        setSelectedType(selectedValue);
    }, []);

    /**
     * Handles filter changes
     * @param {Array} filter - Array of filter objects with columnName and value properties
     */
    const onFilterChangeHandler = useCallback((filter) => {
        document.querySelector('button[aria-label="Go to page 1"]')?.click(); //ITS AN INTERIM FIX SINCE DT DO NOT SUPPORT DATGRID
        setFilter(filter);
    }, []);

    /**
     * Handles back button click
     * Navigates back to the previous page
     * @returns {void}
     */
    const handleBackClick = useCallback(() => {
        navigate('/feeds');
    }, [navigate]);

    /**
     * Sets the columns configuration based on available locations
     * @param {Array} locations - Array of location objects
     */
    const setColumnsHandler = useCallback((locations) => {
        setColumns(selectedType === 'Announcements' ? getAnnouncementAuditColumns(locations) : getConversationAuditColumns(locations));
    }, [selectedType]);
    
    const getModalTitle = useCallback(() => {
        return selectedType === 'Announcements' ? 'Delete Announcement' : 'Delete Message';
    }, [selectedType]);

    const getModalDescription = useCallback(() => {
        return selectedType === 'Announcements' ? 'Are you sure you want to delete the selected announcement?' : 'Are you sure you want to delete the selected message?';
    }, [selectedType]);

    /**
     * Gets FeedDrawer props based on the selected type
     * @returns {Object} FeedDrawer props object
     */
    const getFeedDrawerProps = useCallback(() => {
        const isAnnouncements = selectedType === 'Announcements';
        return {
            open: drawerOpen,
            onClose: handleCloseDrawer,
            drawerType: isAnnouncements ? 'announcement' : 'audit-conversations',
            announcementData: isAnnouncements ? selectedAnnouncementData : null,
            conversationData: !isAnnouncements ? selectedConversationData : null,
            onAnnouncementDeleteHandler: isAnnouncements ? handleDrawerAnnouncementDelete : undefined,
        };
    }, [selectedType, drawerOpen, handleCloseDrawer, selectedAnnouncementData, selectedConversationData, handleDrawerAnnouncementDelete]);

    /**
     * Handles loading audit data based on dependency changes
     * Only loads if this is the first time OR if dependencies actually changed
     */
    const handleAuditDataLoad = useCallback(() => {
        const currentDeps = normalizeAuditDependencies(
            currentPage,
            pageSize,
            sortColumn,
            sortDirection,
            selectedType,
            filter
        );
        
        const depsChanged = hasAuditDependenciesChanged(currentDeps, prevDepsRef.current);
        
        if (!hasLoadedOnce.current || depsChanged) {
            if (!hasLoadedOnce.current) {
                hasLoadedOnce.current = true;
            }
            prevDepsRef.current = currentDeps;
            loadAudit();
        }
    }, [currentPage, pageSize, sortColumn, sortDirection, selectedType, filter, loadAudit]);

    /**
     * Handles LOCATIONS initialization and state reset
     * Only resets state if LOCATIONS or selectedType actually changed
     */
    const handleLocationsUpdate = useCallback(() => {
        if (LOCATIONS.length > 0) {
            const locationsChanged = prevLocationsRef.current !== LOCATIONS;
            const selectedTypeChanged = prevSelectedTypeRef.current !== selectedType;
            
            if (!locationsInitialized.current) {
                // Only initialize columns on first load, don't reset state
                setColumnsHandler(LOCATIONS);
                locationsInitialized.current = true;
                prevLocationsRef.current = LOCATIONS;
                prevSelectedTypeRef.current = selectedType;
            } else if (locationsChanged || selectedTypeChanged) {
                // Only reset state if LOCATIONS or selectedType actually changed after initial load
                document.querySelector('button[data-testid="clear-all-filters-button"]')?.click();
                setData([]);
                setTotalRecordsCount(0);
                setCurrentPage(1);
                setPageSize(10);
                setSortColumn('createdAt');
                setSortDirection('desc');
                setFilter([]);
                setColumnsHandler(LOCATIONS);
                prevLocationsRef.current = LOCATIONS;
                prevSelectedTypeRef.current = selectedType;
            } else {
                // Just update columns if nothing changed
                setColumnsHandler(LOCATIONS);
            }
        }
    }, [LOCATIONS, setColumnsHandler, selectedType]);

    //EFFECTS
    useEffect(() => {
        handleAuditDataLoad();
    }, [handleAuditDataLoad]);
    
    useEffect(() => {
        handleLocationsUpdate();
    }, [handleLocationsUpdate])

    return {
        modalRef,
        columns,
        data,
        pageSize,
        loading,
        selectedType,
        totalRecordsCount,
        drawerOpen,
        selectedAnnouncementData,
        selectedConversationData,
        onTogglaModalHandlerHandler,
        onContinueDeleteModalHandlerHandler,
        onFilterChangeHandler,
        onTypeChangeHandler,
        handleSelectedChangeHandler,
        refreshContactsHandler,
        onPageChangeHandler,
        onPageSizeChangeHandler,
        onSortChangeHandler,    
        handleBackClick,
        handleOpenDrawer,
        handleOpenConversationDrawer,
        handleCloseDrawer,
        handleDrawerAnnouncementDelete,
        getModalTitle,
        getModalDescription,
        getFeedDrawerProps,
    }
};